import axios from 'axios'
import { defineStore } from 'pinia'

const errorMessage = (error, fallback) => {
  const validationErrors = error.response?.data?.errors

  if (validationErrors && typeof validationErrors === 'object') {
    const messages = Object.values(validationErrors).flat().filter(Boolean)

    if (messages.length > 0) {
      return messages[0]
    }
  }

  return error.response?.data?.message ?? fallback
}

export const useCompanyUserStore = defineStore('holidaysPublicCompanyUsers', {
  state: () => ({
    companies: [],
    companySearch: '',
    companyPagination: {
      currentPage: 1,
      lastPage: 1,
      total: 0,
    },
    selectedCompany: null,
    users: [],
    userPagination: {
      currentPage: 1,
      lastPage: 1,
      total: 0,
    },
    isLoadingCompanies: false,
    isLoadingUsers: false,
    isAssigning: false,
    removingUserIds: [],
    error: null,
  }),

  actions: {
    async fetchCompanies(page = 1) {
      this.isLoadingCompanies = true
      this.error = null

      try {
        const { data } = await axios.get('/api/v1/holidays-public/companies', {
          params: {
            page,
            search: this.companySearch || undefined,
          },
        })
        this.companies = data.data
        this.companyPagination = {
          currentPage: data.meta.current_page,
          lastPage: data.meta.last_page,
          total: data.meta.total,
        }

        return data.data
      } catch (error) {
        this.companies = []
        this.error = errorMessage(error, 'Unable to load companies.')
        throw error
      } finally {
        this.isLoadingCompanies = false
      }
    },

    async selectCompany(company) {
      this.selectedCompany = company
      this.users = []
      await this.fetchUsers(1)
    },

    async fetchUsers(page = 1) {
      if (!this.selectedCompany) {
        return
      }

      this.isLoadingUsers = true
      this.error = null

      try {
        const { data } = await axios.get(`/api/v1/holidays-public/companies/${this.selectedCompany.id}/users`, {
          params: { page },
        })
        this.users = data.data
        this.userPagination = {
          currentPage: data.meta.current_page,
          lastPage: data.meta.last_page,
          total: data.meta.total,
        }
        this.updateSelectedCompanyCount(data.meta.total)
      } catch (error) {
        this.users = []
        this.error = errorMessage(error, 'Unable to load assigned users.')
        throw error
      } finally {
        this.isLoadingUsers = false
      }
    },

    async assignUser(user) {
      this.isAssigning = true
      this.error = null

      try {
        await axios.post(`/api/v1/holidays-public/companies/${this.selectedCompany.id}/users`, {
          user_id: user.id,
        })
        await this.fetchUsers(1)
      } catch (error) {
        this.error = errorMessage(error, 'Unable to assign the user.')
        throw error
      } finally {
        this.isAssigning = false
      }
    },

    async removeUser(user) {
      this.removingUserIds.push(user.id)
      this.error = null

      try {
        await axios.delete(`/api/v1/holidays-public/companies/${this.selectedCompany.id}/users/${user.id}`)
        const nextPage = this.users.length === 1 && this.userPagination.currentPage > 1
          ? this.userPagination.currentPage - 1
          : this.userPagination.currentPage
        await this.fetchUsers(nextPage)
      } catch (error) {
        this.error = errorMessage(error, 'Unable to remove the user assignment.')
        throw error
      } finally {
        this.removingUserIds = this.removingUserIds.filter(id => id !== user.id)
      }
    },

    updateSelectedCompanyCount(total) {
      this.selectedCompany.users_count = total
      const company = this.companies.find(item => item.id === this.selectedCompany.id)

      if (company) {
        company.users_count = total
      }
    },
  },
})
